import React from 'react';
import { BarChart3, Bot, Check, Cpu, Headphones, KeyRound, LifeBuoy, Megaphone, MessageSquare, Receipt, Route, Sparkles, TrendingUp, Users } from 'lucide-react';
import { IntegrationLogo } from '../IntegrationLogo';
import { PixelCursor } from '../alt/PixelCursor';

/**
 * THE ARTEFACTS ON THE AI AGENTS PAGE ARE NOT SCREENSHOTS.
 *
 * They are idealised product objects: the same components the real UI uses,
 * but with larger type, fewer elements and no chrome, so each one is legible
 * at a third of a screen and reads as ONE designed thing rather than a photo
 * of a window. A literal screenshot shrunk to this size loses its own labels,
 * which is the failure mode this file exists to avoid.
 *
 * They all share the site's surface language: white fill, the warm #E7E0D4
 * hairline, a 22px radius on the outer objects, and the two-part shadow used
 * by every card on the home page.
 */

export const CARD_SHADOW = 'shadow-[0_1px_2px_rgba(43,33,26,0.05),0_18px_40px_-18px_rgba(43,33,26,0.22)]';

/**
 * Category chips. Colour is FUNCTIONAL here, not decorative — it is how an
 * operator scans a directory of agents for the ones that belong to their
 * department, so this is the one place on the site where a second and third
 * hue are earned.
 *
 * They were originally held at very low saturation, which defeated the point:
 * six chips within a few percent of each other in both hue and chroma cannot
 * be told apart at a glance, so they stopped being a scanning aid and just
 * added grey noise to every card. Each ground is now a clear, distinct tint
 * and each label is a saturated version of the same hue, held dark enough to
 * clear 4.5:1 on its own ground. Sales stays the brand orange, since sales is
 * the department the page's own examples lead with.
 */
const CATEGORIES = {
  sales: {
    label: 'Sales',
    icon: TrendingUp,
    className: 'bg-[#FFE6D2] text-[#B03E06]'
  },
  marketing: {
    label: 'Marketing',
    icon: Megaphone,
    className: 'bg-[#D6F2E0] text-[#0F6B45]'
  },
  support: {
    label: 'Support',
    icon: LifeBuoy,
    className: 'bg-[#DAEAFC] text-[#14549C]'
  },
  ops: {
    label: 'Operations',
    icon: BarChart3,
    className: 'bg-[#FCEAC4] text-[#8A5A05]'
  },
  people: {
    label: 'People',
    icon: Users,
    className: 'bg-[#E8DFFB] text-[#5732B8]'
  },
  finance: {
    label: 'Finance',
    icon: Receipt,
    className: 'bg-[#D2F0EC] text-[#0B6961]'
  }
} as const;
export type AgentCategory = keyof typeof CATEGORIES;
export type AgentTemplate = {
  category: AgentCategory;
  name: string;
  blurb: string;
  /** Catalog slugs — resolved through the shared logo chain. */
  tools: [string, string][];
};

/**
 * Real recurring responsibilities rather than demo filler: each is a job a
 * person currently does by hand on a schedule, which is the whole argument for
 * giving it an owner.
 */
export const AGENT_TEMPLATES: AgentTemplate[] = [{
  category: 'sales',
  name: 'Renewal brief agent',
  blurb: 'Builds a brief before every renewal call from the CRM, the email thread and past notes.',
  tools: [['salesforce', 'Salesforce'], ['gmail', 'Gmail']]
}, {
  category: 'marketing',
  name: 'Campaign recap agent',
  blurb: 'Posts weekly performance with the top movers and what changed since last week.',
  tools: [['hubspot', 'HubSpot'], ['googleanalytics', 'Analytics']]
}, {
  category: 'support',
  name: 'Escalation watcher',
  blurb: 'Flags conversations going quiet and files the ticket with the full context attached.',
  tools: [['intercom', 'Intercom'], ['linear', 'Linear']]
}, {
  category: 'ops',
  name: 'Monday recap agent',
  blurb: "Reads the week's meetings and posts what moved, what slipped and who is waiting.",
  tools: [['notion', 'Notion'], ['slack', 'Slack']]
}, {
  category: 'people',
  name: 'Onboarding guide',
  blurb: 'Walks new starters through setup and answers from the handbook as they go.',
  tools: [['notion', 'Notion'], ['slack', 'Slack']]
}, {
  category: 'finance',
  name: 'Invoice chaser',
  blurb: 'Chases unpaid invoices on a schedule and logs every reply against the account.',
  tools: [['xero', 'Xero'], ['gmail', 'Gmail']]
}];

/** The chip. */
export function CategoryChip({
  category
}: {
  category: AgentCategory;
}) {
  const meta = CATEGORIES[category];
  const Icon = meta.icon;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ${meta.className}`} data-id="element-190" data-mp="21b0b8n8j2vvs">
      <Icon size={13} strokeWidth={2.4} data-id="element-191" data-mp="26bebhj3w11j9" />
      {meta.label}
    </span>;
}

/**
 * ONE AGENT, as it appears in the directory. This is the page's atomic unit —
 * it is the hero overlay, it tiles into the template wall, and it is what the
 * sharing panel lists. Using the same object in all three places is what makes
 * the page read as one product rather than five illustrations.
 */
export function AgentCard({
  template,
  className = ''
}: {
  template: AgentTemplate;
  className?: string;
}) {
  return <div className={`rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${className}`} data-id="element-192" data-mp="pwv6hs0hqhze">
      <CategoryChip category={template.category} data-id="element-193" data-mp="idlwbwk5uf9a" />
      <p className="font-title mt-3 text-[17px] font-bold tracking-[-0.01em] text-[#2B211A] md:text-[18px]" data-id="element-194" data-mp="dkkd5ccx7tjw">
        {template.name}
      </p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-[#6B5D52] md:text-[13.5px]" data-id="element-195" data-mp="8lzcjbtrb7ux">{template.blurb}</p>
      <div className="mt-4 flex items-center gap-2" data-id="element-196" data-mp="4i1fd6nzs74i">
        {template.tools.map(([slug, name]) => <span key={slug} className="flex size-8 items-center justify-center rounded-full border border-[#EFE7DA] bg-white" data-id="element-197" data-mp="119lp39kypmtx">
            <IntegrationLogo slug={slug} name={name} className="size-[17px]" rounded="rounded" data-id="element-198" data-mp="s9ow4vrji1t2" />
          </span>)}
      </div>
    </div>;
}

/**
 * THE TEMPLATE WALL — a spotlight onto a larger directory.
 *
 * There are MORE of these than fit, and the artefact's whole job is to say so.
 * The earlier version said it with a hard crop inside a bordered, rounded
 * frame — but a frame ANSWERS the question: cards stop because the box stops,
 * so it reads as a picture of six cards rather than a window onto hundreds.
 *
 * So there is no frame. The grid is oversized and bled past its column on
 * BOTH sides, then a single radial mask dissolves it into the section's own
 * ground. Nothing terminates the artwork except light falling off, which is
 * what makes the eye assume the wall continues.
 *
 * Two deliberate details:
 *   · The lit region sits high and slightly left of centre, over the middle
 *     card. A centred spotlight reads as a vignette effect; an off-centre one
 *     reads as a camera pointed at something.
 *   · The card under that light is the only one at full weight. Its neighbours
 *     sit back, so a dense grid has one thing to actually look at.
 *
 * The mask is a real mask rather than an overlay: these cards are not
 * interactive, and a scrim painted on top would have to match the ground
 * colour exactly or read as haze sitting over the page.
 */

/** Off-centre, high. Shared by the mask and the board so they fall off together. */
const WALL_LIGHT = 'radial-gradient(118% 95% at 46% 32%, #000 0%, #000 36%, rgba(0,0,0,0.74) 57%, rgba(0,0,0,0.3) 76%, transparent 90%)';
export function TemplateWall() {
  // Bled hard to the OUTER edge and only slightly inward: this artefact sits
  // in the right-hand half of its beat, so a symmetric bleed would push cards
  // under the copy on its left.
  // Two full rows, wide cards. Three narrow columns in a half-width column
  // made a portrait block; two wide ones over the same bled width give each
  // card room for its own type and land the artefact close to square.
  return <div aria-hidden="true" className="relative w-full select-none" style={{
    maskImage: WALL_LIGHT,
    WebkitMaskImage: WALL_LIGHT
  }} data-id="element-199" data-mp="2lttqf8pb23jm">
      {/* The board the directory is ruled on. No fill and no border — only the
       grid — so the light is the only thing defining where it ends. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.55]" style={{
      backgroundImage: 'linear-gradient(to right, #E7DCCB 1px, transparent 1px), linear-gradient(to bottom, #E7DCCB 1px, transparent 1px)',
      backgroundSize: '72px 72px'
    }} data-id="element-200" data-mp="2pxt5dmuxsuav" />

      {/* Four templates share one horizontal shelf. The parent gives this an
       intentionally wide canvas before reducing the complete object, so each
       card retains a readable internal scale instead of being squeezed into a
       narrow four-column grid. */}
      <div className="relative grid grid-cols-4 gap-5 px-4 py-8 md:gap-6 md:px-6 md:py-10" data-id="element-201" data-mp="3ij9n6v7ssvhj">
        {AGENT_TEMPLATES.slice(0, 4).map((template, index) => {
        // The second card sits beneath the off-centre spotlight.
        const lit = index === 1;
        return <div key={template.name} className="relative" data-id="element-202" data-mp="3sbfctyakv0tk" data-mpi={index}>
              <AgentCard template={template} className={lit ? 'shadow-[0_2px_4px_rgba(43,33,26,0.06),0_26px_60px_-24px_rgba(43,33,26,0.34)]' : 'opacity-[0.72] shadow-[0_1px_2px_rgba(43,33,26,0.04),0_12px_28px_-18px_rgba(43,33,26,0.16)]'} data-id="element-203" data-mp="2utfacdnhb6xa" />
              {/* The pointer names WHICH card the claim is about. Sat on the
               lit card's lower-left corner, half on and half off it, so it
               reads as resting over the directory rather than as an icon
               placed inside a card. */}
              {lit && <PixelCursor className="pointer-events-none absolute -bottom-5 -left-4 h-11 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)] md:-bottom-6 md:-left-5 md:h-14" data-id="element-204" data-mp="2e9gbrzk7wg3h" />}
            </div>;
      })}
      </div>
    </div>;
}

/** Integrations, as toggles. Two on, the rest waiting — the state a workspace
 * is actually in on day one, which is more honest than an all-green list. */
const CONNECTIONS: [string, string, boolean][] = [['slack', 'Slack', true], ['salesforce', 'Salesforce', true], ['notion', 'Notion', false], ['gmail', 'Gmail', false], ['jira', 'Jira', false]];
export function ConnectionsCard() {
  return <div className={`w-full max-w-[230px] rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${CARD_SHADOW}`} data-id="element-205" data-mp="1lnexo2ig647m">
      <p className="font-title text-[16px] font-bold tracking-[-0.02em] text-[#2B211A]" data-id="element-206" data-mp="1nzzaj7zlem5e">Connections</p>
      <ul className="mt-2.5" data-id="element-207" data-mp="22ovp7nw5mxez">
        {CONNECTIONS.map(([slug, name, on]) => <li key={slug} className="flex items-center gap-2 border-b border-[#F1EAE0] py-2 last:border-0" data-id="element-208" data-mp="22o3suauqgvnn">
            <span aria-hidden="true" className={`flex h-[18px] w-[31px] flex-none items-center rounded-full px-[3px] transition-colors ${on ? 'justify-end bg-[#EA580C]' : 'justify-start bg-[#E7E0D4]'}`} data-id="element-209" data-mp="1xrvirbve7snc">
              <span className="flex size-3 items-center justify-center rounded-full bg-white" data-id="element-210" data-mp="153hl4x87uycb">
                {on && <Check size={8} strokeWidth={3.5} className="text-[#EA580C]" data-id="element-211" data-mp="1jw1c6zi3dfde" />}
              </span>
            </span>
            <IntegrationLogo slug={slug} name={name} className="size-[18px]" rounded="rounded" data-id="element-212" data-mp="2vdau3hlknzcl" />
            <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-213" data-mp="2qeq225gh4lz8">{name}</span>
          </li>)}
      </ul>
    </div>;
}

/**
 * THE APPROVAL PROMPT. The most load-bearing artefact on the page: it is the
 * only one that shows the agent NOT acting. Three actions in ascending trust —
 * decline, approve once, approve always — because the middle one is the choice
 * most people make first and it should not be the loud one.
 *
 * THE ACTION HAS TO BE ONE WORTH STOPPING FOR. This used to ask permission to
 * post a Slack message, which quietly argued the opposite of its point: if the
 * gate fires on something that harmless, it reads as friction on every action
 * rather than as judgement about the few that matter. Raising a campaign's
 * daily budget spends real money and cannot be un-spent, so the pause is
 * obviously right — and the sheet shows the old value beside the new one,
 * because an approval you cannot check is a rubber stamp.
 */
export function ApprovalCard({
  /**
   * 'glass' lays the prompt ON a photograph: a frosted panel holding a solid
   * white data sheet. The frost is what makes it read as a system prompt
   * surfacing over what the person is doing, rather than a card sitting on a
   * picture — and the data sheet stays opaque because it is the part you have
   * to actually read before you press anything.
   */
  variant = 'card'
}: {
  variant?: 'card' | 'glass';
} = {}) {
  const glass = variant === 'glass';
  return <div className={glass ? 'w-full rounded-[16px] border border-white/45 bg-white/45 p-3.5 shadow-[0_1px_2px_rgba(43,33,26,0.06),0_26px_60px_-22px_rgba(43,33,26,0.4)] backdrop-blur-2xl' : `w-full max-w-[300px] rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${CARD_SHADOW}`} data-id="element-214" data-mp="44mibw0lql3h">
      <div className="flex items-start gap-3" data-id="element-215" data-mp="3mjn6qiiwez09">
        <span className={`flex size-9 flex-none items-center justify-center rounded-xl bg-white ${glass ? 'shadow-[0_1px_2px_rgba(43,33,26,0.12)]' : 'border border-[#EFE7DA]'}`} data-id="element-216" data-mp="3hjr1kfygt1j4">
          <IntegrationLogo slug="googleads" name="Google Ads" className="size-[18px]" rounded="rounded" data-id="element-217" data-mp="3cmseuih4rwuy" />
        </span>
        <p className="text-[13px] font-semibold leading-snug text-[#2B211A]" data-id="element-218" data-mp="2xhwm0yna7a9p">
          The campaign agent wants to raise a daily budget
        </p>
      </div>

      <dl className={`mt-3 text-[11.5px] ${glass ? 'rounded-xl bg-white p-3' : 'rounded-xl bg-[#FAF8F4] p-3'}`} data-id="element-219" data-mp="32ewgsdeokqli">
        <div className="flex gap-4" data-id="element-220" data-mp="37dr700ez9hfn">
          <dt className="w-[70px] flex-none font-semibold text-[#8A7B6D]" data-id="element-221" data-mp="3cfgljq1esd8h">Campaign</dt>
          <dd className="text-[#2B211A]" data-id="element-222" data-mp="2erexl69hf0u5">Q4 Enterprise · Search</dd>
        </div>
        <div className="mt-3 flex gap-4" data-id="element-223" data-mp="2epbwnr6sjnan">
          <dt className="w-[70px] flex-none font-semibold text-[#8A7B6D]" data-id="element-224" data-mp="2jrb0t3acuo8n">Daily cap</dt>
          {/* The old figure struck through beside the new one: the number is
           the decision, so it has to be checkable at a glance. */}
          <dd className="flex items-baseline gap-1.5 text-[#2B211A]" data-id="element-225" data-mp="2thdt119njy5x">
            <span className="text-[#8A7B6D] line-through" data-id="element-226" data-mp="1vzm5de28ljnh">$400</span>
            <span className="font-bold" data-id="element-227" data-mp="20zi8lb34171z">$1,200</span>
          </dd>
        </div>
        <div className="mt-3 flex gap-4" data-id="element-228" data-mp="20xfae3rt07m6">
          <dt className="w-[70px] flex-none font-semibold text-[#8A7B6D]" data-id="element-229" data-mp="6kl64y4xsy0d">Why</dt>
          <dd className="leading-relaxed text-[#2B211A]" data-id="element-230" data-mp="1kyqyj5jt7ox">
            Cost per demo fell to $84 and the campaign has capped out four days running.
          </dd>
        </div>
      </dl>

      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-231" data-mp="biwfz8v8f8il">
        <button type="button" className={`whitespace-nowrap rounded-full border border-[#E4B9A2] px-3 py-1.5 text-[10.5px] font-bold text-[#B4470F] transition-colors ${glass ? 'bg-white/70 hover:bg-white' : 'hover:bg-[#FDF0E6]'}`} data-id="element-232" data-mp="bhbgr5xz31ee">
          Decline
        </button>
        <span className="grow" data-id="element-233" data-mp="z75wbv9of42c" />
        <button type="button" className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[10.5px] font-bold text-[#2B211A] transition-colors ${glass ? 'bg-white hover:bg-white/90' : 'border border-[#E7E0D4] hover:bg-[#FAF8F4]'}`} data-id="element-234" data-mp="z64jpmd0i9ii">
          Always allow
        </button>
        <button type="button" className="whitespace-nowrap rounded-full bg-[#EA580C] px-3.5 py-1.5 text-[10.5px] font-bold text-white transition-colors hover:bg-[#D14E0A]" data-id="element-235" data-mp="2w1sitqcda49t">
          Approve
        </button>
      </div>
    </div>;
}

/** A group owns more than agents: it carries the reusable resources needed
 * to keep ongoing work, methods and access available to everyone in it. */
const SHARED_RESOURCES = [{
  type: 'Agent',
  icon: Bot,
  name: 'Renewal brief',
  meta: '18 runs'
}, {
  type: 'Track',
  icon: Route,
  name: 'Renewal risk & customer health',
  meta: 'Live'
}, {
  type: 'Skill',
  icon: Sparkles,
  name: 'Executive account brief',
  meta: '42 uses'
}, {
  type: 'Credential',
  icon: KeyRound,
  name: 'Salesforce',
  meta: 'Managed'
}];
export function SharedAgentsCard() {
  return <div className="relative pl-4 pt-4 sm:pl-8 sm:pt-6" data-id="element-236" data-mp="ntnhd0ns9xgh">
      {/* Offset sheets keep the sense of a shared library without adding tabs
       or a category switcher that the compact export cannot meaningfully use. */}
      <div aria-hidden="true" className="absolute left-0 top-0 hidden h-[86%] w-[92%] rounded-[26px] bg-[#F2EBE1] sm:block" data-id="element-237" data-mp="2i4spoi9tey25" />
      <div aria-hidden="true" className="absolute left-3 top-2.5 hidden h-[92%] w-[94%] -rotate-[1.4deg] rounded-[26px] border border-[#EFE7DA] bg-[#FBF6EF] shadow-[0_18px_44px_-28px_rgba(43,33,26,0.3)] sm:block" data-id="element-238" data-mp="2bqmufzjhyien" />

      <div className={`relative overflow-hidden rounded-[22px] border border-[#EFE7DA] bg-white ${CARD_SHADOW}`} data-id="element-239" data-mp="22qgi4gzjboh2">
        <header className="flex items-start justify-between gap-4 border-b border-[#EFE7DA] px-6 py-5" data-id="element-240" data-mp="2zf61kbqt8p55">
          <div className="min-w-0" data-id="element-241" data-mp="2ui5vw1xvhoek">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#A99A8C]" data-id="element-242" data-mp="2uj7hv1gffvb2">Group library</p>
            <h3 className="font-title mt-1.5 text-[22px] font-bold tracking-[-0.02em] text-[#2B211A]" data-id="element-243" data-mp="2po1ojhkp3ecy">Revenue</h3>
            <p className="mt-1 text-[12px] text-[#8A7B6D]" data-id="element-244" data-mp="3i6p64n0hgb9a">Available to everyone in the group</p>
          </div>
          <div className="shrink-0 text-right" data-id="element-245" data-mp="3dfgudkbokdsy">
            <p className="text-[23px] font-bold leading-none tabular-nums text-[#2B211A]" data-id="element-246" data-mp="3dep8j17vxufv">24</p>
            <p className="mt-1.5 text-[10.5px] font-medium text-[#A99A8C]" data-id="element-247" data-mp="zyxcpkzcx6z5">shared resources</p>
          </div>
        </header>

        <ul className="px-6 py-1.5" data-id="element-248" data-mp="14ya5emcwt89n">
          {SHARED_RESOURCES.map(({
          type,
          icon: Icon,
          name,
          meta
        }) => <li key={type} className="grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 border-b border-[#F5EFE6] py-3 last:border-0" data-id="element-249" data-mp="190djzjuvksz5">
              <span aria-hidden="true" className="flex size-9 items-center justify-center rounded-xl bg-[#FAF8F4] text-[#8A7B6D]" data-id="element-250" data-mp="1iv5j4s43vnw1">
                <Icon size={16} strokeWidth={2} data-id="element-251" data-mp="1ntgu566504fu" />
              </span>
              <span className="min-w-0" data-id="element-252" data-mp="1sn2vw3arinwv">
                <span className="block text-[10.5px] font-semibold text-[#A99A8C]" data-id="element-253" data-mp="1sody3yok7nlv">{type}</span>
                <span className="mt-0.5 block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-254" data-mp="1wppoi6f9ccv4">{name}</span>
              </span>
              <span className="whitespace-nowrap rounded-full bg-[#FAF8F4] px-2.5 py-1 text-[10.5px] font-semibold text-[#6B5D52]" data-id="element-255" data-mp="26l9l6v1nbfkx">{meta}</span>
            </li>)}
        </ul>
      </div>
    </div>;
}

/**
 * THE BUILDER — two overlapping sheets. The editor states what the agent is
 * for; the settings sheet in front of it states what it is allowed to use. Two
 * objects rather than one tall form, because the overlap is what says these
 * are panels of a live tool.
 */
export function AgentBuilderCard() {
  return <div className="relative pb-7 pr-5 sm:pr-10" data-id="element-256" data-mp="yrcsedd76p6j">
      <div className={`w-full max-w-[275px] rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${CARD_SHADOW}`} data-id="element-257" data-mp="yq1p0ennzjfy">
        <div className="flex items-center gap-3" data-id="element-258" data-mp="tr8caog7bwgu">
          <span aria-hidden="true" className="flex size-9 flex-none items-center justify-center rounded-xl bg-[#FDF0E6] text-[#EA580C]" data-id="element-259" data-mp="17l9clmmsxrjb">
            <TrendingUp size={16} strokeWidth={2.3} data-id="element-260" data-mp="12rwoddm1bmr3" />
          </span>
          <p className="font-title text-[16px] font-bold leading-tight tracking-[-0.02em] text-[#2B211A]" data-id="element-261" data-mp="f2lveiaxewni">
            Renewal brief agent
          </p>
        </div>

        <p className="mt-4 text-[11px] font-semibold text-[#8A7B6D]" data-id="element-262" data-mp="y3gzon0d28n">Instructions</p>
        <p className="mt-1.5 rounded-xl bg-[#FAF8F4] p-3 text-[11.5px] leading-relaxed text-[#2B211A]" data-id="element-263" data-mp="5p3bkn8nn87z">
          Before every renewal call, pull the account's history, the open threads and the last
          three meetings, and write a one-page brief.
        </p>

        {/* The pointer lands on the one control that commits the whole sheet.
         Everything above it is description; this is the moment the agent
         exists, which is what "in minutes" beside it is claiming. */}
        <div className="relative mt-5" data-id="element-264" data-mp="9qyl2f0qorwu">
          <button type="button" className="w-full rounded-full bg-[#EA580C] py-2.5 text-[12px] font-bold text-white transition-colors hover:bg-[#D14E0A]" data-id="element-265" data-mp="9px0njxu2t2g">
            Create agent
          </button>
          <PixelCursor className="pointer-events-none absolute -bottom-4 left-[38%] h-8 w-auto drop-shadow-[0_6px_10px_rgba(43,33,26,0.3)]" data-id="element-266" data-mp="2ug4jjfrfxxdm" />
        </div>
      </div>

      {/* In front and to the right, overlapping by about a third. */}
      <div className={`absolute bottom-0 right-0 w-[145px] rounded-[16px] border border-[#EFE7DA] bg-white p-3.5 ${CARD_SHADOW}`} data-id="element-267" data-mp="1z9fwvr7o1go3">
        <p className="text-[11px] font-semibold text-[#8A7B6D]" data-id="element-268" data-mp="1qbdqsx92rxty">Model</p>
        <p className="mt-1.5 flex items-center gap-1.5 rounded-full border border-[#EFE7DA] px-2.5 py-1 text-[10.5px] font-semibold text-[#2B211A]" data-id="element-269" data-mp="1qd6xudnlok2h">
          <Cpu size={12} className="text-[#8A7B6D]" data-id="element-270" data-mp="1gjzvz2s2jm75" />
          Your choice
        </p>

        <p className="mt-3 text-[11px] font-semibold text-[#8A7B6D]" data-id="element-271" data-mp="1lc13kbeakoqu">Runs</p>
        <p className="mt-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold text-[#2B211A]" data-id="element-272" data-mp="17kmky5oaqpkb">
          <MessageSquare size={12} className="text-[#8A7B6D]" data-id="element-273" data-mp="1blolqqxv39oe" />
          On a Pulse
        </p>

        <p className="mt-3 text-[11px] font-semibold text-[#8A7B6D]" data-id="element-274" data-mp="xmglrvtvjxj5">Connections</p>
        <div className="mt-1.5 flex items-center gap-1" data-id="element-275" data-mp="xojamcjajkma">
          {[['salesforce', 'Salesforce'], ['gmail', 'Gmail'], ['slack', 'Slack'], ['notion', 'Notion']].map(([slug, name]) => <span key={slug} className="flex size-6 items-center justify-center rounded-full border border-[#EFE7DA] bg-white" data-id="element-276" data-mp="izqac1mfsoju">
              <IntegrationLogo slug={slug} name={name} className="size-[13px]" rounded="rounded" data-id="element-277" data-mp="sxnvuhcww78r" />
            </span>)}
        </div>
      </div>
    </div>;
}